/**
 * Syncs Resources selection from controller → display via kiosk loopback relay.
 * Controller publishes a resource key; display opens the detail modal.
 * Regular / non-kiosk: no-op.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useKioskRole } from './useKioskRole'
import {
  getKioskState,
  patchKioskPayload,
  subscribeKioskState,
  type KioskStateEnvelope,
} from '../utils/kioskDisplaySync'

export type ResourcesKioskSnapshot = {
  selectedKey: string | null
  /** Bumped on each controller click so re-selecting the same item re-opens on display. */
  selectionToken: number
}

function readResources(envelope: KioskStateEnvelope | null): ResourcesKioskSnapshot | null {
  const raw = envelope?.state?.payload?.resources
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  return raw as ResourcesKioskSnapshot
}

export function useResourcesKioskSync(args: {
  /** Display: open/clear detail from remote selection. */
  onRemoteSelection: (selectedKey: string | null, selectionToken: number) => void
}): {
  isDisplayKiosk: boolean
  isControllerKiosk: boolean
  /** Controller highlight key (also mirrors last published selection). */
  controllerSelectedKey: string | null
  publishSelection: (selectedKey: string | null) => void
} {
  const role = useKioskRole()
  const isDisplayKiosk = role === 'display'
  const isControllerKiosk = role === 'controller'
  const [controllerSelectedKey, setControllerSelectedKey] = useState<string | null>(null)
  const selectionTokenRef = useRef(0)
  const onRemoteRef = useRef(args.onRemoteSelection)
  onRemoteRef.current = args.onRemoteSelection
  const lastAppliedTokenRef = useRef<number | null>(null)

  const publishSelection = useCallback(
    (selectedKey: string | null) => {
      if (!isControllerKiosk) return
      selectionTokenRef.current += 1
      const next: ResourcesKioskSnapshot = {
        selectedKey,
        selectionToken: selectionTokenRef.current,
      }
      setControllerSelectedKey(selectedKey)
      void patchKioskPayload({ resources: next }, 'controller')
    },
    [isControllerKiosk],
  )

  // Display: follow controller selection
  useEffect(() => {
    if (!isDisplayKiosk) return

    const apply = (envelope: KioskStateEnvelope | null) => {
      const snap = readResources(envelope)
      if (!snap) return
      const key = typeof snap.selectedKey === 'string' ? snap.selectedKey : null
      const token = typeof snap.selectionToken === 'number' ? snap.selectionToken : 0
      if (lastAppliedTokenRef.current === token) return
      lastAppliedTokenRef.current = token
      onRemoteRef.current(key, token)
    }

    void getKioskState().then(apply)
    return subscribeKioskState(apply)
  }, [isDisplayKiosk])

  return {
    isDisplayKiosk,
    isControllerKiosk,
    controllerSelectedKey,
    publishSelection,
  }
}
